import { Grid, Typography } from '@mui/material';
import React, { useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { subOrderSchema } from 'src/config/yup';
import { t } from 'i18next';
import { showDate2, showErrorTost, showSuccessTost } from 'src/constants';
import CustomTextField from '../formFields/textField';
import MenuPopover from '../menu/menu';
import { ICONS } from 'src/assets/library';
import { useModal } from 'src/hooks/useModal';
import { DELETE_SUBORDER, UPDATE_SUBORDER } from 'src/apiService/apiDeclaration';
import EditSubOrderForm from './EditSubOrderForm';
import HorizontalStepper from '../stepper/HorizontalStepper';
import Invoice2 from '../pdfs/Invoice2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TransportBill from '../pdfs/TransportBill';
import AddInventoryForm from './AddInventoryForm';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'src/config/enum';
import LoaderButton from '../loaderButton/loaderButton';

const SubOrder = ({
  order,
  isSuccessfull,
  currentStatus,
  subOrder,
  setSubOrder,
  customerInfo,
  onSuccess,
  onUpdateOrder,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { setDialogData, handleModalClose } = useModal();

  // Memoize form default values to prevent recalculation on every render
  const defaultValues = useMemo(
    () => ({
      ...order,
      deliveryDate: showDate2(order.deliveryDate),
    }),
    [order]
  );

  const {
    control,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(subOrderSchema),
    defaultValues,
  });

  const price = watch('perCubicMeterConcretePrice');

  // Memoize functions to prevent recreation on every render
  const deleteOrder = useCallback(
    async (id) => {
      try {
        const resp = await DELETE_SUBORDER(id);
        onSuccess();
        showSuccessTost(resp.message);
      } catch (error) {
        showErrorTost(error);
      }
    },
    [onSuccess]
  );

  // Optimize useEffect to prevent excessive updates
  useEffect(() => {
    const updatedOrder = getValues();
    const existingOrderIndex = subOrder.findIndex((sub) => sub._id === updatedOrder._id);

    if (existingOrderIndex !== -1) {
      setSubOrder((prevSubOrders) => {
        const updatedSubOrders = prevSubOrders.map((sub, index) =>
          index === existingOrderIndex ? { ...sub, ...updatedOrder } : sub
        );
        return updatedSubOrders;
      });
    } else {
      setSubOrder((prevSubOrders) => [...prevSubOrders, updatedOrder]);
    }
  }, [price, getValues, setSubOrder, subOrder]);

  const generatePdf = useCallback(
    (i) => {
      if (i === 1) {
        const input = document.getElementById('invoice2');
        input.style.display = 'flex';
        html2canvas(input, { scale: 2 }).then((canvas) => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p', 'mm', 'a4');
          pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
          pdf.save(`Purchase_Order_${order.orderNumber}_${String(order.subOrderNumber).padStart(4, '0')}.pdf`);
        });
        input.style.display = 'none';
      } else {
        const input = document.getElementById('bill');
        input.style.display = 'flex';
        html2canvas(input, { scale: 2 }).then((canvas) => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('l', 'mm', 'a4');
          pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
          pdf.save(`Transport_Bill_${order.orderNumber}_${String(order.subOrderNumber).padStart(4, '0')}.pdf`);
        });
        input.style.display = 'none';
      }
    },
    [order.orderNumber, order.subOrderNumber]
  );
  return (
    <Grid item xs={12}>
      <form>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" color={'#ACD629'} my={2}>
            {t('orderDetailsPage.subOrder')} # {String(order.subOrderNumber).padStart(4, '0')}
          </Typography>
          {user.role !== USER_ROLES.CUSTOMER && (
            <MenuPopover
              sx={{ maxWidth: '300px' }}
              menuArray={[
                ...(user.role === USER_ROLES.CEO || user.role === USER_ROLES.ADMINISTRATOR
                  ? [
                      {
                        icon: ICONS.delete,
                        name: t('menu.deleteOrder'),
                        action: () => {
                          setDialogData({
                            showModal: true,
                            title: subOrder.length > 1 ? '' : t('orderDetailsPage.subOrderDelErrorTitle'),
                            description:
                              subOrder.length > 1
                                ? t('common.delConfirmationOrder')
                                : t('orderDetailsPage.subOrderDelError'),
                            child:
                              subOrder.length > 1 ? (
                                <Grid
                                  container
                                  sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}
                                  xs={12}
                                >
                                  <LoaderButton
                                    sx={{ width: 'max-content' }}
                                    buttonText={'Confirm'}
                                    onClick={() => deleteOrder(order._id)}
                                  />
                                </Grid>
                              ) : (
                                <Grid
                                  container
                                  sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}
                                  xs={12}
                                >
                                  <LoaderButton
                                    sx={{ width: 'max-content' }}
                                    buttonText={'Close'}
                                    onClick={() => handleModalClose()}
                                  />
                                </Grid>
                              ),
                          });
                        },
                      },
                      {
                        icon: ICONS.edit,
                        name: t('menu.editItem'),
                        action: () =>
                          setDialogData({
                            showModal: true,
                            title: t('menu.editItem'),
                            child: (
                              <EditSubOrderForm
                                order={order}
                                onSuccess={onSuccess}
                                isSuccessfull={isSuccessfull}
                                onUpdateOrder={onUpdateOrder}
                              />
                            ),
                          }),
                      },
                    ]
                  : []),
                ...((user.role === USER_ROLES.OPERATOR ||
                  user.role === USER_ROLES.ADMINISTRATOR ||
                  user.role === USER_ROLES.CEO) &&
                order.status[order.status.length - 1].status === 'IN_PROGRESS'
                  ? [
                      {
                        icon: ICONS.prepare,
                        name: t('orderDetailsPage.prepareOrder'),
                        action: () =>
                          setDialogData({
                            showModal: true,
                            title: t('orderDetailsPage.prepareOrder'),
                            child: <AddInventoryForm onSuccess={() => onSuccess()} stockAdd={false} data={order} />,
                          }),
                      },
                    ]
                  : []),
                ...((user.role === USER_ROLES.OPERATOR ||
                  user.role === USER_ROLES.ADMINISTRATOR ||
                  user.role === USER_ROLES.CEO) &&
                order.status[order.status.length - 1].status === 'PREPARING'
                  ? [
                      {
                        icon: ICONS.readyForDispatched,
                        name: t('labels.READY_FOR_DISPATCHED'),
                        action: () =>
                          setDialogData({
                            showModal: true,
                            description: t('orderDetailsPage.RFDConfirm'),
                            onConfirm: async () => {
                              try {
                                let resp = await UPDATE_SUBORDER(order._id, {
                                  ...order,
                                  status: 'READY_FOR_DISPATCHED',
                                });
                                showSuccessTost(resp.message);
                                onSuccess();
                                onUpdateOrder();
                              } catch (err) {
                                showErrorTost(err.message);
                              }
                            },
                          }),
                      },
                    ]
                  : []),
                {
                  icon: ICONS.download,
                  name: t('menu.downloadPurchaseOrder'),
                  action: () => generatePdf(1),
                },
                // {
                //   icon: ICONS.download,
                //   name: t('invoice.transportBill'),
                //   action: () => generatePdf(2),
                // },
              ]}
            />
          )}
        </Grid>
        <Grid container xs={12} spacing={4}>
          <Invoice2 data={{ ...order, customerInfo: customerInfo }} />
          <TransportBill />
          {(currentStatus === 'PAYMENT_RECEIVED' || currentStatus === 'COMPLETED') && (
            <Grid item xs={12} my={2}>
              <HorizontalStepper status={order?.status} isSubOrder={true} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('orderDetailsPage.subOrderStatus')}
              value={t(`labels.${order.status[order.status.length - 1].status}`)}
              controller={{
                name: 'status',
                control: control,
                errors: errors.status,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.dosage')}
              controller={{
                name: 'dosage',
                control: control,
                errors: errors.dosage,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.volumeOfConcrete')}
              controller={{
                name: 'volumeOfConcrete',
                control: control,
                errors: errors.volumeOfConcrete,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.deliveryDate')}
              controller={{
                name: 'deliveryDate',
                control: control,
                errors: errors.deliveryDate,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.stAddress')}
              type="name"
              controller={{
                name: 'streetAddress',
                control: control,
                errors: errors.streetAddress,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.stNo')}
              type="name"
              controller={{
                name: 'streetNumber',
                control: control,
                errors: errors.streetNumber,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.municipality')}
              type="name"
              controller={{
                name: 'municipality',
                control: control,
                errors: errors.municipality,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.city')}
              type="name"
              controller={{
                name: 'city',
                control: control,
                errors: errors.city,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.deliveryTime')}
              type="name"
              value={t(`labels.${order.timeOfDelivery}`)}
              controller={{
                name: 'timeOfDelivery',
                control: control,
                errors: errors.timeOfDelivery,
              }}
              disabled
            />
          </Grid>
          {isSuccessfull && user.role !== USER_ROLES.OPERATOR && (
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label={t('customerPage.concretePrice(pcm)')}
                type="number"
                controller={{
                  name: 'perCubicMeterConcretePrice',
                  control: control,
                  errors: errors.perCubicMeterConcretePrice,
                }}
                disabled={order.perCubicMeterConcretePrice !== undefined}
              />
            </Grid>
          )}
        </Grid>
      </form>
    </Grid>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(SubOrder, (prevProps, nextProps) => {
  // Custom comparison function to determine if re-render is needed
  return (
    prevProps.order._id === nextProps.order._id &&
    prevProps.order.subOrderNumber === nextProps.order.subOrderNumber &&
    prevProps.isSuccessfull === nextProps.isSuccessfull &&
    prevProps.currentStatus === nextProps.currentStatus &&
    prevProps.customerInfo?._id === nextProps.customerInfo?._id &&
    prevProps.onSuccess === nextProps.onSuccess &&
    prevProps.onUpdateOrder === nextProps.onUpdateOrder
  );
});
